/* LL 110: pure cart writes, selection rules and batching are exported for mocked-cart tests. */
((scope) => {
  const propertyIsSet = (item, name) => {
    const value = item?.properties?.[name];
    return value != null && String(value).trim() !== '';
  };
  const isSelectable = item => !item?.protected && !item?.gift && !item?.freeSauce && !propertyIsSet(item, '_ll_gift') && !propertyIsSet(item, '_ll_free_sauce');
  const selectableKeys = cart => (cart?.items || []).filter(isSelectable).map(item => item.key);
  const updatesFor = (cart, selectedKeys) => {
    const allowed = new Set(selectableKeys(cart));
    return [...new Set(selectedKeys || [])].reduce((updates, key) => {
      if (allowed.has(key)) updates[key] = 0;
      return updates;
    }, {});
  };
  const endpoint = (root, path) => `${String(root || '/').replace(/\/?$/, '/')}${path}`;
  const readJson = async response => {
    if (!response?.ok) {
      const error = new Error('Cart request failed');
      // Only a client response confirms that Shopify rejected this write. A 5xx
      // can arrive after the mutation was accepted, just like a fetch failure.
      const status = Number(response?.status) || 0;
      error.cartKnownNotApplied = status >= 400 && status < 500;
      error.status = response?.status;
      throw error;
    }
    return response.json();
  };
  const cartRequest = async ({ fetcher, root = '/', path, body, sectionId, sectionsUrl }) => {
    const hasBody = body !== undefined;
    const payload = hasBody ? { ...body } : undefined;
    if (payload && sectionId) {
      payload.sections = [sectionId];
      payload.sections_url = sectionsUrl || '/';
    }
    const response = await fetcher(endpoint(root, path), hasBody ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
    } : { cache: 'no-store', headers: { Accept: 'application/json' } });
    return readJson(response);
  };
  const remove = async ({ fetcher, root = '/', mode, selectedKeys = [], cart, sectionId, sectionsUrl }) => {
    if (mode === 'all') {
      return cartRequest({
        fetcher, root, path: 'cart/clear.js', body: {}, sectionId, sectionsUrl
      });
    }
    const current = cart || await cartRequest({ fetcher, root, path: 'cart.js' });
    const updates = updatesFor(current, selectedKeys);
    if (!Object.keys(updates).length) return current;
    return cartRequest({
      fetcher, root, path: 'cart/update.js', body: { updates }, sectionId, sectionsUrl
    });
  };
  const addFlavour = async ({ fetcher, root = '/', variantId, available = true, sectionId, sectionsUrl }) => {
    if (!available || !(Number(variantId) > 0)) throw new Error('Sauce flavour unavailable');
    return cartRequest({
      fetcher, root, path: 'cart/add.js', body: { items: [{ id: Number(variantId), quantity: 1 }] }, sectionId, sectionsUrl
    });
  };
  const sauceState = ({ items = [], productIds = [], minimum = 3 } = {}) => {
    const ids = new Set(productIds.map(String));
    const flavours = new Set();
    let quantity = 0;
    items.forEach(item => {
      if (!ids.has(String(item.product_id)) || propertyIsSet(item, '_ll_gift') || propertyIsSet(item, '_ll_free_sauce')) return;
      quantity += Number(item.quantity) || 0;
      flavours.add(String(item.variant_id || item.id));
    });
    return { quantity, flavours: [...flavours], showAnotherFlavour: quantity > 0 && flavours.size === 1, blocked: quantity > 0 && quantity < Math.max(1, Number(minimum) || 3) };
  };
  const normalProperties = properties => Object.entries(properties || {})
    .filter(([, value]) => value != null)
    .map(([name, value]) => [String(name), String(value)])
    .sort(([a], [b]) => a.localeCompare(b));
  const lineIdentity = item => ({
    variantId: Number(item?.variant_id ?? item?.variant ?? item?.id) || 0,
    sellingPlanId: Number(item?.selling_plan_allocation?.selling_plan?.id ?? item?.selling_plan ?? item?.sellingPlan) || 0,
    properties: normalProperties(item?.properties)
  });
  const identityKey = item => {
    if (item?.identityKey) return item.identityKey;
    const identity = item?.identity || lineIdentity(item);
    // Old fixtures do not carry variant data. Production entries always do, but
    // retaining their key fallback keeps the pure selection API backwards compatible.
    return identity.variantId ? JSON.stringify(identity) : `key:${item?.key || ''}`;
  };
  const resolveRemovalEntries = (entries, cart) => {
    const remaining = [...(cart?.items || [])];
    return (entries || []).map(entry => {
      const stableKey = identityKey(entry);
      const index = remaining.findIndex(item => stableKey.startsWith('key:') ? item.key === entry.key : identityKey(item) === stableKey);
      if (index < 0) return { ...entry, identityKey: stableKey, missing: true };
      const [item] = remaining.splice(index, 1);
      return { ...entry, key: item.key, quantity: Number(item.quantity) || Number(entry.quantity) || 1, identityKey: stableKey, missing: false };
    });
  };
  const createRemovalQueue = ({ commit, onOptimistic = () => {}, onSuccess = () => {}, onError = () => {}, onIdle = () => {}, wait = 70 }) => {
    const waiting = new Map();
    const activeIds = new Set();
    let contexts = [], active = false, timer;
    const schedule = delay => {
      if (active || timer || !waiting.size) return;
      timer = setTimeout(() => { timer = undefined; drain(); }, delay);
    };
    const enqueue = (entries, context = {}) => {
      const fresh = [];
      (entries || []).forEach(entry => {
        const id = identityKey(entry);
        if (!entry?.key || waiting.has(id) || activeIds.has(id)) return;
        entry.identityKey = id;
        waiting.set(id, entry);
        fresh.push(entry);
      });
      if (!fresh.length) return;
      contexts.push(context);
      onOptimistic(fresh, context);
      schedule(wait);
    };
    const drain = async () => {
      if (timer) { clearTimeout(timer); timer = undefined; }
      if (active || !waiting.size) return;
      const entries = [...waiting.values()];
      entries.forEach(entry => activeIds.add(entry.identityKey));
      const batchContexts = contexts;
      waiting.clear(); contexts = []; active = true;
      let outcome = 'success';
      try {
        const result = await commit(entries, batchContexts);
        await onSuccess(result, entries, batchContexts);
      } catch (error) {
        outcome = 'error';
        await onError(error, entries, batchContexts);
      } finally {
        entries.forEach(entry => activeIds.delete(entry.identityKey));
        active = false;
        if (waiting.size) schedule(0);
        else {
          try { await onIdle({ outcome }); } catch (_) { /* The owning cart state handles its own recovery. */ }
        }
      }
    };
    return { enqueue, flush: drain, pendingEntries: () => [...waiting.values()], active: () => active };
  };
  const core = { isSelectable, selectableKeys, updatesFor, cartRequest, remove, addFlavour, sauceState, lineIdentity, identityKey, resolveRemovalEntries, createRemovalQueue };
  scope.LLCartSelectionCore = core;
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
})(typeof globalThis === 'undefined' ? this : globalThis);

/* LL: enhance Local's drawer; all HTML refreshes still use renderHelperCart. */
(() => {
  if (typeof document === 'undefined') return;
  // LL: merchant copy comes from Liquid's JSON; values remain text, never HTML.
  const llText = (key, fallback, values = []) => {
    let copy;
    if (typeof document !== 'undefined') {
      const node = document.getElementById('LLCopy');
      if (node) {
        if (!node.llCopy) { try { node.llCopy = JSON.parse(node.textContent); } catch (_) { node.llCopy = {}; } }
        copy = node.llCopy[key];
      }
    }
    return String(copy == null ? fallback : copy).replace(/\[(\d+)\]/g, (token, n) => n <= values.length ? String(values[n - 1]) : token);
  };

  const drawer = document.querySelector('#site-cart-sidebar.ll-cart');
  if (!drawer || drawer.dataset.llReady) return;
  drawer.dataset.llReady = 'true';
  const root = window.Shopify?.routes?.root || '/';
  let busy = false;
  let swapping = false;
  let failed = false;
  let pending = false;
  let pendingRefresh = false;
  let ownRender = false;
  let message = '';
  let cartToken;
  let previousFocus;
  let opened = false;
  let dockObserver;
  const helperSectionId = `${window.KT_FILE_PREFIX || ''}helper-cart`;
  const sectionsUrl = () => window.location.pathname || '/';
  let lastSectionHtml = '';
  let removalQueue;
  const queuedMutations = [];
  const optimisticRemovals = new Map();
  let optimisticHeaderBase;
  function syncDockHeight() {
    const dock = drawer.querySelector('.ll-cart-footer__dock');
    const height = dock?.getBoundingClientRect().height || 0;
    if (height) drawer.style.setProperty('--ll-cart-dock-height', `${Math.ceil(height)}px`);
  }
  function decorateShell() {
    const header = drawer.querySelector('.sidebar__header');
    const close = header?.querySelector('[data-js-close]');
    if (close && !close.querySelector('.ll-cart-close-label')) {
      const label = document.createElement('span');
      label.className = 'll-cart-close-label';
      label.textContent = llText('ll_js_cart_close', 'Close');
      close.insertBefore(label, close.querySelector('svg, [aria-hidden="true"]'));
    }
    if (typeof ResizeObserver === 'function') {
      dockObserver?.disconnect();
      const dock = drawer.querySelector('.ll-cart-footer__dock');
      if (dock) {
        dockObserver = new ResizeObserver(syncDockHeight);
        dockObserver.observe(dock);
      }
    }
    syncDockHeight();
  }
  const removedTiers = new Set();
  const removalKey = (cart, tier) => `ll-gift-removed:${cart.token}:${tier}`;
  function declined(cart, tier, remember = false) {
    if (!cart.token) return true; // No stable cart identity: do not auto-add.
    const key = removalKey(cart, tier);
    if (remember) removedTiers.add(key);
    try {
      if (remember) localStorage.setItem(key, '1');
      return removedTiers.has(key) || localStorage.getItem(key) === '1';
    } catch (_) { return removedTiers.has(key); }
  }
  function restoreAutomaticGifts(cart, tier) {
    const key = removalKey(cart, tier);
    removedTiers.delete(key);
    try { localStorage.removeItem(key); } catch (_) { /* In-memory state still restored. */ }
  }
  const config = () => JSON.parse(drawer.querySelector('[data-ll-cart-config]')?.textContent || '{}');
  const localBusy = () => !!document.querySelector('.cart__form.processing, #AjaxCartSubtotal.processing');
  const surfaces = () => [document.querySelector('[data-ll-cart-page]'), drawer].filter(Boolean);
  const selectedLineKeys = new Set();
  const confirmReturn = new WeakMap();
  const selectableRows = surface => [...surface.querySelectorAll('[data-js-cart-item][data-ll-selectable]')];
  const removalActive = () => optimisticRemovals.size > 0 || !!removalQueue?.active();
  const mutationQueued = () => queuedMutations.length > 0;
  const cartModel = () => ({
    items: (config().lines || []).map(line => ({
      key: line.key,
      quantity: line.quantity,
      protected: line.protected,
      gift: line.gift
    }))
  });
  function closeDeleteConfirm(bar, restoreFocus = false) {
    const confirm = bar?.querySelector('[data-ll-delete-confirm]');
    if (!confirm || confirm.hidden) return;
    confirm.hidden = true;
    delete bar.dataset.llDeleteMode;
    if (restoreFocus) confirmReturn.get(bar)?.focus({ preventScroll: true });
  }
  function paintSelection() {
    const available = new Set(surfaces().flatMap(surface => selectableRows(surface).map(row => row.dataset.id)));
    [...selectedLineKeys].forEach(key => { if (!available.has(key)) selectedLineKeys.delete(key); });
    const sauceState = window.LLFreeSauces?.getCheckoutState?.() || {};
    const locked = busy || mutationQueued() || removalActive() || sauceState.pending || sauceState.running || localBusy() || failed;
    surfaces().forEach(surface => {
      const rows = selectableRows(surface);
      const keys = rows.map(row => row.dataset.id);
      const selected = keys.filter(key => selectedLineKeys.has(key));
      rows.forEach(row => {
        const input = row.querySelector('[data-ll-line-select]');
        const checked = selectedLineKeys.has(row.dataset.id);
        if (!input) return;
        if (input.checked !== checked) input.checked = checked;
        if (input.disabled !== locked) input.disabled = locked;
      });
      surface.querySelectorAll('[data-ll-cart-selection]').forEach(bar => {
        const selectAll = bar.querySelector('[data-ll-select-all]');
        if (selectAll) {
          const checked = keys.length > 0 && selected.length === keys.length;
          const indeterminate = selected.length > 0 && selected.length < keys.length;
          if (selectAll.checked !== checked) selectAll.checked = checked;
          if (selectAll.indeterminate !== indeterminate) selectAll.indeterminate = indeterminate;
          if (selectAll.disabled !== locked) selectAll.disabled = locked;
        }
        const count = bar.querySelector('[data-ll-selected-count]');
        // LL: write only when the text changes; an unchanged rewrite still counts as a DOM change and fed a redraw loop that froze the page.
        const countText = `${selected.length} selected`;
        if (count && count.textContent !== countText) count.textContent = countText;
        const selectedButton = bar.querySelector('[data-ll-delete-selected]');
        const selectedDisabled = locked || selected.length === 0;
        if (selectedButton && selectedButton.disabled !== selectedDisabled) selectedButton.disabled = selectedDisabled;
        const allButton = bar.querySelector('[data-ll-delete-all]');
        if (allButton && allButton.disabled !== locked) allButton.disabled = locked;
        bar.querySelectorAll('[data-ll-delete-confirm-yes], [data-ll-delete-confirm-cancel]').forEach(button => {
          if (button.disabled !== locked) button.disabled = locked;
        });
        const confirm = bar.querySelector('[data-ll-delete-confirm]');
        if (confirm && !confirm.hidden && bar.dataset.llDeleteMode === 'selected' && selected.length === 0) closeDeleteConfirm(bar);
      });
    });
  }
  function openDeleteConfirm(button, mode) {
    const bar = button.closest('[data-ll-cart-selection]');
    if (!bar || busy || removalActive() || localBusy() || failed) return;
    const surface = bar.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart');
    const selectedCount = selectableRows(surface).filter(row => selectedLineKeys.has(row.dataset.id)).length;
    const count = mode === 'all' ? Number(bar.dataset.llTotalLines || 0) : selectedCount;
    if (count < 1) return;
    bar.dataset.llDeleteMode = mode;
    const confirm = bar.querySelector('[data-ll-delete-confirm]');
    const prompt = bar.querySelector('[data-ll-delete-prompt]');
    if (prompt) prompt.textContent = mode === 'all' ? `Delete all ${count} ${count === 1 ? 'item' : 'items'}?` : `Delete ${count} selected ${count === 1 ? 'item' : 'items'}?`;
    if (confirm) confirm.hidden = false;
    confirmReturn.set(bar, button);
    bar.querySelector('[data-ll-delete-confirm-yes]')?.focus({ preventScroll: true });
  }
  function confirmSelection(bar) {
    const mode = bar?.dataset.llDeleteMode;
    if (!bar || !['selected', 'all'].includes(mode) || busy || localBusy() || removalActive()) return;
    const surface = bar.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart');
    const onPage = !!surface?.matches('[data-ll-cart-page]');
    const keys = mode === 'all'
      ? (config().lines || []).map(line => line.key)
      : selectableRows(surface).map(row => row.dataset.id).filter(key => selectedLineKeys.has(key));
    if (!keys.length) return;
    actionAnchor = captureAnchor(bar);
    closeDeleteConfirm(bar, false);
    queueRemovals(keys, { mode, onPage, selection: true });
  }
  const meals = surface => JSON.parse(surface.querySelector('[data-ll-meal-config]')?.textContent || '{}');
  const sauceBlocked = surface => {
    const { sauces = 0, sauceMin = 3, freeSaucesEarned = 0 } = meals(surface);
    return Number(freeSaucesEarned) < 1 && sauces > 0 && sauces < sauceMin;
  };
  const freeSauceState = () => {
    const state = window.LLFreeSauces?.getCheckoutState?.() || { blocked: false, message: '', reason: '' };
    if (state.blocked || !document.querySelector('[data-ll-free-sauce-paid]:not([hidden])')) return state;
    const issue = "Your free sauces aren't applying to this basket. Please refresh. If it stays, remove the free sauces or contact us.";
    return { ...state, blocked: true, paid: true, message: state.message || issue, reason: state.reason || issue };
  };
  const uncommittedQuantity = () => [...document.querySelectorAll('[data-js-cart-item]')].some(row => { const input = row.querySelector('.qty'); return input && Number(input.value) !== Number(row.dataset.qty); });
  const checkoutHardLocked = surface => uncommittedQuantity() || busy || mutationQueued() || removalActive() || localBusy() || failed || freeSauceState().blocked || sauceBlocked(surface) || Number(meals(surface).singles || 0) % 15 !== 0 || meals(surface).items === 0;
  const checkoutTermsLocked = surface => !!surface.querySelector('#AgreeToTos:not(:checked)');
  const checkoutLocked = surface => checkoutHardLocked(surface) || checkoutTermsLocked(surface);
  const basis = cart => cart.items.reduce((sum, item) => sum + (item.properties?._ll_gift ? 0 : item.final_line_price), 0);
  const tierCount = (cart, tier) => cart.items.reduce((sum, item) => sum + (String(item.properties?._ll_gift) === String(tier) ? item.quantity : 0), 0);
  const money = cents => `R${(Number(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2, maximumFractionDigits: 2
  })}`;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const addrowFrames = new WeakMap();
  function updateAddrow(track) {
    if (!track?.isConnected) return;
    const band = track.closest('[data-ll-addrow]');
    if (!band) return;
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const overflow = maximum > 2;
    const start = !overflow || track.scrollLeft <= 2;
    const end = !overflow || track.scrollLeft >= maximum - 2;
    const previous = band.querySelector('[data-ll-addrow-arrow="-1"]');
    const next = band.querySelector('[data-ll-addrow-arrow="1"]');
    [[previous, start], [next, end]].forEach(([button, disabled]) => {
      if (!button) return;
      if (button.hidden !== !overflow) button.hidden = !overflow;
      if (button.disabled !== disabled) button.disabled = disabled;
    });
    const progress = band.querySelector('[data-ll-addrow-progress]');
    if (!progress) return;
    if (progress.hidden !== !overflow) progress.hidden = !overflow;
    const value = progress.querySelector('[data-ll-addrow-progress-value]');
    if (!value || !overflow) return;
    const size = Math.max(12, Math.min(100, track.clientWidth / track.scrollWidth * 100));
    const position = maximum > 0 ? Math.max(0, Math.min(1, track.scrollLeft / maximum)) * (100 - size) : 0;
    const width = `${size.toFixed(3)}%`;
    const left = `${position.toFixed(3)}%`;
    if (value.style.width !== width) value.style.width = width;
    if (value.style.left !== left) value.style.left = left;
  }
  function scheduleAddrow(track) {
    if (!track || addrowFrames.has(track)) return;
    const frame = requestAnimationFrame(() => {
      addrowFrames.delete(track);
      updateAddrow(track);
    });
    addrowFrames.set(track, frame);
  }
  function addrowDistance(track) {
    const card = track?.querySelector('.ll-cart-addrow__card');
    if (!card) return track?.clientWidth || 0;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    const step = card.getBoundingClientRect().width + gap;
    const visible = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    return step * visible;
  }
  function scrollAddrow(control, direction) {
    const track = control?.closest('[data-ll-addrow]')?.querySelector('[data-ll-addrow-track]');
    if (!track) return;
    track.scrollBy({ left: addrowDistance(track) * direction, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }
  function initAddrows() {
    document.querySelectorAll('[data-ll-addrow-track]').forEach(track => {
      if (track.dataset.llAddrowReady !== 'true') {
        track.dataset.llAddrowReady = 'true';
        track.addEventListener('scroll', () => scheduleAddrow(track), { passive: true });
      }
      scheduleAddrow(track);
    });
  }
  function addFromAddrow(button) {
    const variantId = Number(button.dataset.llCartAdd);
    if (!(variantId > 0) || !window.LLCartMutations?.enqueue) return;
    const addedText = button.dataset.llAddedText || 'Added';
    window.LLCartMutations.enqueue(async ({ request: queuedRequest, refresh: queuedRefresh }) => {
      await queuedRequest('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
      const label = button.querySelector('[data-ll-addrow-button-text]');
      if (button.isConnected && label && label.textContent !== addedText) label.textContent = addedText;
      message = config().added;
      paint();
      await new Promise(resolve => setTimeout(resolve, 650));
      const cart = await queuedRequest('cart.js');
      await queuedRefresh();
      return { cart };
    }, { source: 'addrow', variantId }).catch(() => {});
  }

  // LL 127LB: show the offer only after every existing checkout lock has passed.
  let b2g1Reminder;
  let b2g1ReminderConfig;
  let b2g1ReminderPending;
  let b2g1ReminderReturn;
  let b2g1ReminderBypass = false;
  const b2g1ReminderKey = count => `ll-b2g1-reminder:${count}`;
  const reminderElement = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  function readB2g1ReminderConfig(surface) {
    const node = surface?.querySelector('[data-ll-b2g1-reminder-config]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (_) { return null; }
  }
  function b2g1ReminderDismissed(count, set = false) {
    try {
      const key = b2g1ReminderKey(count);
      if (set) sessionStorage.setItem(key, '1');
      return sessionStorage.getItem(key) === '1';
    } catch (_) { return false; }
  }
  function shouldShowB2g1Reminder(surface) {
    const cfg = readB2g1ReminderConfig(surface);
    const count = Number(cfg?.count) || 0;
    return !!(cfg?.enabled && cfg?.reminderEnabled && count % 3 === 2 && cfg.products?.length && !b2g1ReminderDismissed(count));
  }
  function reminderSurface(kind) {
    return kind === 'page' ? document.querySelector('[data-ll-cart-page]') : drawer;
  }
  function closeB2g1Reminder(returnFocus = true) {
    if (!b2g1Reminder || b2g1Reminder.hidden) return;
    b2g1Reminder.hidden = true;
    document.documentElement.classList.remove('ll-b2g1-reminder-open');
    if (returnFocus) {
      const surface = reminderSurface(b2g1ReminderPending?.surface);
      const checkout = surface?.querySelector('[name="checkout"], #CheckOut');
      (checkout || b2g1ReminderReturn)?.focus?.({ preventScroll: true });
    }
  }
  function continueB2g1Checkout() {
    const pending = b2g1ReminderPending;
    const surface = reminderSurface(pending?.surface);
    const checkout = surface?.querySelector('[name="checkout"], #CheckOut');
    const form = checkout?.form || surface?.querySelector('form.cart__form');
    // Re-read the rendered gate before replaying. Every existing lock paints the
    // checkout button disabled; the terms check is kept explicit for safety.
    if (!surface || checkout?.disabled || checkoutTermsLocked(surface)) {
      closeB2g1Reminder(true);
      paint();
      return;
    }
    closeB2g1Reminder(false);
    b2g1ReminderBypass = true;
    try {
      if (checkout && !checkout.disabled) checkout.click();
      else if (form) form.requestSubmit();
    } finally {
      queueMicrotask(() => { b2g1ReminderBypass = false; });
    }
  }
  function dismissB2g1Reminder() {
    if (b2g1ReminderConfig) b2g1ReminderDismissed(Number(b2g1ReminderConfig.count) || 0, true);
    continueB2g1Checkout();
  }
  function setB2g1ReminderStage(stage) {
    if (!b2g1Reminder) return;
    b2g1Reminder.querySelector('[data-ll-reminder-prompt]').hidden = stage !== 'prompt';
    b2g1Reminder.querySelector('[data-ll-reminder-picker]').hidden = stage !== 'picker';
    b2g1Reminder.querySelector('[data-ll-reminder-confirmation]').hidden = stage !== 'confirmation';
    const status = b2g1Reminder.querySelector('[data-ll-reminder-status]');
    if (status) { status.hidden = true; status.textContent = ''; }
    b2g1Reminder.querySelector('.ll-b2g1-reminder__panel').scrollTop = 0;
  }
  function selectB2g1Variant(card, button) {
    if (!card || !button || button.disabled) return;
    card.dataset.llReminderVariant = button.dataset.llReminderVariant;
    card.querySelectorAll('[data-ll-reminder-variant]').forEach(tile => tile.setAttribute('aria-checked', String(tile === button)));
    const image = card.querySelector('[data-ll-reminder-product-image]');
    if (image && button.dataset.image) {
      image.src = button.dataset.image;
      image.alt = button.dataset.imageAlt || card.dataset.productTitle || '';
    }
    const price = card.querySelector('[data-ll-reminder-price]');
    if (price) price.textContent = button.dataset.money || '';
  }
  function renderB2g1ReminderProducts() {
    const host = b2g1Reminder.querySelector('[data-ll-reminder-products]');
    host.replaceChildren();
    for (const product of b2g1ReminderConfig.products || []) {
      const selected = product.variants.find(variant => variant.available);
      if (!selected) continue;
      const card = reminderElement('article', 'll-b2g1-reminder__product');
      card.dataset.llReminderVariant = String(selected.id);
      card.dataset.productTitle = product.title;
      const image = reminderElement('img', 'll-b2g1-reminder__product-media');
      image.dataset.llReminderProductImage = '';
      image.src = selected.image || product.image || '';
      image.alt = selected.imageAlt || product.imageAlt || product.title;
      image.width = 68; image.height = 68; image.loading = 'lazy';
      const copy = reminderElement('div', 'll-b2g1-reminder__product-copy');
      copy.append(reminderElement('h3', '', product.title));
      const price = reminderElement('strong', 'll-b2g1-reminder__price', selected.money);
      price.dataset.llReminderPrice = '';
      copy.append(price);
      card.append(image, copy);
      const showTiles = product.variants.length > 1 || !/^default title$/i.test(product.variants[0]?.title || '');
      if (showTiles) {
        const tiles = reminderElement('div', 'll-b2g1-reminder__tiles');
        tiles.setAttribute('role', 'radiogroup');
        tiles.setAttribute('aria-label', product.title);
        for (const variant of product.variants) {
          const label = variant.available ? variant.title : `${variant.title} - ${b2g1ReminderConfig.text.soldOut}`;
          const tile = reminderElement('button', 'll-b2g1-reminder__tile');
          tile.type = 'button';
          tile.dataset.llReminderVariant = String(variant.id);
          tile.dataset.image = variant.image || product.image || '';
          tile.dataset.imageAlt = variant.imageAlt || product.imageAlt || product.title;
          tile.dataset.money = variant.money;
          tile.setAttribute('role', 'radio');
          tile.setAttribute('aria-checked', String(variant.id === selected.id));
          tile.disabled = !variant.available;
          const tileImage = reminderElement('img');
          tileImage.src = variant.image || product.image || '';
          tileImage.alt = '';
          tileImage.width = 34; tileImage.height = 34; tileImage.loading = 'lazy';
          tile.append(tileImage, reminderElement('span', '', label));
          tiles.append(tile);
        }
        card.append(tiles);
      }
      const add = reminderElement('button', 'll-b2g1-reminder__button ll-b2g1-reminder__button--primary ll-b2g1-reminder__product-add', b2g1ReminderConfig.text.add);
      add.type = 'button';
      add.dataset.llReminderAdd = '';
      card.append(add);
      host.append(card);
    }
  }
  function addFromB2g1Reminder(button) {
    const card = button.closest('.ll-b2g1-reminder__product');
    const variantId = Number(card?.dataset.llReminderVariant);
    if (!(variantId > 0) || !window.LLCartMutations?.enqueue) return;
    const buttons = [...b2g1Reminder.querySelectorAll('[data-ll-reminder-add]')];
    buttons.forEach(control => { control.disabled = true; });
    button.textContent = b2g1ReminderConfig.text.adding;
    const quantity = cart => (cart?.items || []).filter(item => Number(item.variant_id) === variantId).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    window.LLCartMutations.enqueue(async ({ request: queuedRequest, refresh: queuedRefresh, cart }) => {
      const before = quantity(cart);
      try {
        await queuedRequest('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
        const truth = await queuedRequest('cart.js');
        await queuedRefresh();
        return { cart: truth, added: quantity(truth) > before };
      } catch (error) {
        let truth = cart;
        try { truth = await queuedRequest('cart.js'); } catch (_) { /* Checkout still uses Shopify's server truth. */ }
        try { await queuedRefresh(); } catch (_) { /* The picker keeps a safe checkout escape. */ }
        return { cart: truth, added: quantity(truth) > before, reminderError: quantity(truth) <= before };
      }
    }, { source: 'b2g1-reminder', variantId }).then(result => {
      if (result?.reminderError || !result?.added) {
        const status = b2g1Reminder.querySelector('[data-ll-reminder-status]');
        status.textContent = b2g1ReminderConfig.text.error;
        status.hidden = false;
        buttons.forEach(control => { control.disabled = false; control.textContent = b2g1ReminderConfig.text.add; });
        return;
      }
      const confirmation = b2g1Reminder.querySelector('[data-ll-reminder-confirmation-text]');
      confirmation.textContent = Number(b2g1ReminderConfig.count) >= 5 ? b2g1ReminderConfig.text.addedMore : b2g1ReminderConfig.text.added;
      setB2g1ReminderStage('confirmation');
      b2g1Reminder.querySelector('[data-ll-reminder-go]')?.focus({ preventScroll: true });
    }).catch(() => {
      const status = b2g1Reminder.querySelector('[data-ll-reminder-status]');
      status.textContent = b2g1ReminderConfig.text.error;
      status.hidden = false;
      buttons.forEach(control => { control.disabled = false; control.textContent = b2g1ReminderConfig.text.add; });
    });
  }
  function openB2g1Reminder(surface, checkout) {
    const cfg = readB2g1ReminderConfig(surface);
    if (!cfg) return;
    b2g1ReminderConfig = cfg;
    b2g1ReminderPending = { surface: surface.matches('[data-ll-cart-page]') ? 'page' : 'drawer' };
    b2g1ReminderReturn = checkout || document.activeElement;
    b2g1Reminder.querySelector('.ll-b2g1-reminder__panel').setAttribute('aria-label', cfg.text.title);
    b2g1Reminder.querySelector('[data-ll-reminder-title]').textContent = cfg.text.title;
    b2g1Reminder.querySelector('[data-ll-reminder-message]').textContent = Number(cfg.count) >= 5 ? cfg.text.moreMessage : cfg.text.message;
    b2g1Reminder.querySelector('[data-ll-reminder-no-thanks]').textContent = cfg.text.noThanks;
    b2g1Reminder.querySelector('[data-ll-reminder-choose]').textContent = cfg.text.choose;
    b2g1Reminder.querySelector('[data-ll-reminder-picker-title]').textContent = cfg.text.pickerTitle;
    b2g1Reminder.querySelector('[data-ll-reminder-skip-picker]').textContent = cfg.text.noThanks;
    b2g1Reminder.querySelector('[data-ll-reminder-go]').textContent = cfg.text.goCheckout;
    b2g1Reminder.querySelector('[data-ll-reminder-back]').textContent = cfg.text.back;
    setB2g1ReminderStage('prompt');
    b2g1Reminder.hidden = false;
    document.documentElement.classList.add('ll-b2g1-reminder-open');
    b2g1Reminder.querySelector('[data-ll-reminder-choose]').focus({ preventScroll: true });
  }
  function initB2g1Reminder() {
    if (b2g1Reminder) return;
    b2g1Reminder = reminderElement('div', 'll-b2g1-reminder');
    b2g1Reminder.hidden = true;
    b2g1Reminder.dataset.llB2g1Reminder = '';
    const panel = reminderElement('div', 'll-b2g1-reminder__panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.tabIndex = -1;
    const prompt = reminderElement('section', 'll-b2g1-reminder__prompt');
    prompt.dataset.llReminderPrompt = '';
    const title = reminderElement('h2');
    title.id = 'll-b2g1-reminder-title'; title.dataset.llReminderTitle = '';
    const messageNode = reminderElement('p'); messageNode.dataset.llReminderMessage = '';
    const promptActions = reminderElement('div', 'll-b2g1-reminder__actions');
    const choose = reminderElement('button', 'll-b2g1-reminder__button ll-b2g1-reminder__button--primary');
    choose.type = 'button'; choose.dataset.llReminderChoose = '';
    const noThanks = reminderElement('button', 'll-b2g1-reminder__button');
    noThanks.type = 'button'; noThanks.dataset.llReminderNoThanks = '';
    promptActions.append(choose, noThanks); prompt.append(title, messageNode, promptActions);
    const picker = reminderElement('section', 'll-b2g1-reminder__picker'); picker.dataset.llReminderPicker = ''; picker.hidden = true;
    const pickerTitle = reminderElement('h3', 'll-b2g1-reminder__picker-heading'); pickerTitle.dataset.llReminderPickerTitle = '';
    const products = reminderElement('div', 'll-b2g1-reminder__products'); products.dataset.llReminderProducts = '';
    const status = reminderElement('p', 'll-b2g1-reminder__status'); status.dataset.llReminderStatus = ''; status.setAttribute('role', 'alert'); status.hidden = true;
    const pickerActions = reminderElement('div', 'll-b2g1-reminder__actions');
    const skipPicker = reminderElement('button', 'll-b2g1-reminder__button'); skipPicker.type = 'button'; skipPicker.dataset.llReminderSkipPicker = '';
    pickerActions.append(skipPicker); picker.append(pickerTitle, products, status, pickerActions);
    const confirmation = reminderElement('section', 'll-b2g1-reminder__confirmation'); confirmation.dataset.llReminderConfirmation = ''; confirmation.hidden = true;
    const confirmationText = reminderElement('p'); confirmationText.dataset.llReminderConfirmationText = '';
    const confirmationActions = reminderElement('div', 'll-b2g1-reminder__actions');
    const go = reminderElement('button', 'll-b2g1-reminder__button ll-b2g1-reminder__button--primary'); go.type = 'button'; go.dataset.llReminderGo = '';
    const back = reminderElement('button', 'll-b2g1-reminder__button'); back.type = 'button'; back.dataset.llReminderBack = '';
    confirmationActions.append(go, back); confirmation.append(confirmationText, confirmationActions);
    panel.append(prompt, picker, confirmation); b2g1Reminder.append(panel); document.body.append(b2g1Reminder);
    b2g1Reminder.addEventListener('click', event => {
      const target = event.target.closest?.('button');
      if (!target) return;
      if (target.matches('[data-ll-reminder-no-thanks], [data-ll-reminder-skip-picker]')) dismissB2g1Reminder();
      else if (target.matches('[data-ll-reminder-choose]')) {
        renderB2g1ReminderProducts(); setB2g1ReminderStage('picker');
        b2g1Reminder.querySelector('[data-ll-reminder-variant]:not(:disabled), [data-ll-reminder-add]')?.focus({ preventScroll: true });
      } else if (target.matches('[data-ll-reminder-variant]')) selectB2g1Variant(target.closest('.ll-b2g1-reminder__product'), target);
      else if (target.matches('[data-ll-reminder-add]')) addFromB2g1Reminder(target);
      else if (target.matches('[data-ll-reminder-go]')) continueB2g1Checkout();
      else if (target.matches('[data-ll-reminder-back]')) closeB2g1Reminder(true);
    });
    b2g1Reminder.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); dismissB2g1Reminder(); return; }
      if (event.key !== 'Tab') return;
      const controls = [...b2g1Reminder.querySelectorAll('button:not(:disabled), [tabindex="0"]')].filter(node => node.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (!first) { event.preventDefault(); panel.focus({ preventScroll: true }); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus({ preventScroll: true }); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus({ preventScroll: true }); }
    });
  }
  function updateGiftHeaders() {
    document.querySelectorAll('[data-ll-gift-header]').forEach(link => {
      const surface = link.dataset.llGiftHeader === 'page' ? document.querySelector('[data-ll-cart-page]') : drawer;
      const hidden = !config().enabled || !surface?.querySelector('[data-ll-unlocked]');
      if (link.hidden !== hidden) link.hidden = hidden;
    });
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-ll-gift-header]');
    if (!link) return;
    event.preventDefault();
    const surface = link.dataset.llGiftHeader === 'page' ? document.querySelector('[data-ll-cart-page]') : drawer;
    const gifts = surface?.querySelector('.ll-cart-gifts');
    if (!gifts) return;
    const body = surface === drawer ? drawer : null;
    const origin = body ? drawer.querySelector('.sidebar__header').getBoundingClientRect().bottom : 0;
    const top = gifts.getBoundingClientRect().top - origin - 8;
    (body || window).scrollBy({ top, behavior: reducedMotion() ? 'instant' : 'smooth' });
    gifts.querySelector('[data-ll-gifts-heading]')?.focus({ preventScroll: true });
  });

  // LL: stable row/card and containing-section anchors survive complete helper replacement.
  let actionAnchor;
  function captureAnchor(target) {
    const surface = target?.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart');
    if (!surface) return null;
    const body = surface === drawer ? drawer : null;
    const offset = () => body ? drawer.querySelector('.sidebar__header').getBoundingClientRect().bottom : 0;
    const candidates = [];
    const row = target.closest('[data-js-cart-item]');
    const tier = row?.dataset.llLineTier;
    if (row) {
      candidates.push({ selector: `[data-id="${CSS.escape(row.dataset.id)}"]`, y: row.getBoundingClientRect().top - offset() });
      if (tier && target.dataset.llCartAdd) {
        const selector = `[data-ll-line-tier="${CSS.escape(tier)}"]:has([data-ll-cart-add="${CSS.escape(target.dataset.llCartAdd)}"][aria-pressed="true"])`;
        candidates.push({ selector, y: row.getBoundingClientRect().top - offset() });
      }
    }
    const card = target.closest('[data-ll-product]');
    if (card) candidates.push({ selector: `[data-ll-product="${CSS.escape(card.dataset.llProduct)}"]`, y: card.getBoundingClientRect().top - offset() });
    const horizontalTrack = target.closest('[data-ll-addrow-track]');
    for (const selector of ['.ll-cart-addrow', '.ll-cart-subscribe', '.ll-cart-gifts', '.ll-cart-meal-box', '.cart__items']) {
      const section = target.closest(selector);
      if (section) candidates.push({ selector, y: section.getBoundingClientRect().top - offset() });
    }
    return { surface, body, candidates, scroll: body ? body.scrollTop : window.scrollY, horizontalScroll: horizontalTrack?.scrollLeft || 0 };
  }
  function restoreAnchor(anchor) {
    if (!anchor) return;
    const { surface, body } = anchor;
    const horizontalTrack = surface.querySelector('[data-ll-addrow-track]');
    if (horizontalTrack) horizontalTrack.scrollLeft = anchor.horizontalScroll || 0;
    for (const candidate of anchor.candidates) {
      const node = surface.querySelectorAll(candidate.selector)[candidate.index || 0];
      if (!node || !node.getClientRects().length) continue;
      const origin = body ? drawer.querySelector('.sidebar__header').getBoundingClientRect().bottom : 0;
      const delta = node.getBoundingClientRect().top - origin - candidate.y;
      (body || window).scrollBy({ top: delta, behavior: 'instant' });
      return;
    }
    (body || window).scrollTo({ top: anchor.scroll, behavior: 'instant' });
  }
  document.addEventListener('ll:cart:before-render', () => {
    if (actionAnchor) return;
    const surface = drawer.classList.contains('sidebar--opened') ? drawer : document.querySelector('[data-ll-cart-page]');
    const top = surface === drawer ? drawer.querySelector('.sidebar__header').getBoundingClientRect().bottom : 0;
    const visible = [...(surface?.querySelectorAll('[data-js-cart-item], .ll-cart-addrow, .ll-cart-gifts') || [])].find(node => node.getBoundingClientRect().bottom > top);
    actionAnchor = captureAnchor(visible);
  });
  document.addEventListener('input', event => {
    if (event.target.matches('.cart__form .qty')) actionAnchor = captureAnchor(event.target);
  }, true);

  // LL: a single persistent live region, independent of the drawer fragments.
  const toast = document.createElement('div');
  toast.id = 'll-cart-unlock-toast';
  toast.className = 'll-cart-toast';
  toast.hidden = true;
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.setAttribute('aria-atomic', 'true');
  toast.innerHTML = '<span class="ll-cart-toast__icon" aria-hidden="true">&#127881;</span><div class="ll-cart-toast__copy"><strong></strong><p></p></div><button type="button" class="ll-cart-toast__close"><svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true"><circle class="ll-cart-toast__track" cx="16" cy="16" r="13"/><circle class="ll-cart-toast__ring" cx="16" cy="16" r="13" pathLength="1"/></svg><span aria-hidden="true">&times;</span></button>';
  document.body.append(toast);
  const toastClose = toast.querySelector('button');
  const ring = toast.querySelector('.ll-cart-toast__ring');
  let remaining = 0, duration = 5000, lastTick = 0, toastFrame, hideTimer, hovering = false, toastReturn;
  function positionToast() {
    if (toast.hidden) return;
    const inDrawer = drawer.classList.contains('sidebar--opened');
    let top = 8, right = window.innerWidth < 600 ? 12 : 16, width = window.innerWidth - right * 2;
    let bottom = window.innerHeight - 8;
    if (inDrawer) {
      const rect = drawer.getBoundingClientRect();
      top = drawer.querySelector('.sidebar__header').getBoundingClientRect().bottom + 8;
      right = Math.max(16, window.innerWidth - rect.right + 16);
      width = rect.width - 32;
      const dock = drawer.querySelector('.ll-cart-footer__dock');
      const dockRect = dock?.getBoundingClientRect();
      if (dockRect && dockRect.top > top && dockRect.top < window.innerHeight) bottom = dockRect.top - 8;
    } else {
      document.querySelectorAll('#site-header, .sticky-header.show').forEach(header => {
        const rect = header.getBoundingClientRect(), style = getComputedStyle(header);
        if (rect.top < window.innerHeight && rect.bottom > 0 && style.visibility !== 'hidden' && style.display !== 'none') top = Math.max(top, rect.bottom + 8);
      });
    }
    toast.style.top = `${top}px`;
    toast.style.right = `${right}px`;
    toast.style.width = `${Math.min(width, inDrawer || window.innerWidth >= 600 ? 380 : width)}px`;
    toast.style.maxHeight = `${Math.max(0, bottom - top)}px`;
  }
  function closeToast(immediate = false) {
    cancelAnimationFrame(toastFrame);
    clearTimeout(hideTimer);
    if (toast.contains(document.activeElement)) {
      const fallback = drawer.classList.contains('sidebar--opened') ? drawer.querySelector('[data-js-close]') : document.querySelector('[data-ll-gift-header="page"]');
      (toastReturn?.isConnected ? toastReturn : fallback)?.focus({ preventScroll: true });
    }
    toast.classList.remove('is-visible');
    if (immediate) toast.hidden = true;
    else hideTimer = setTimeout(() => { toast.hidden = true; }, reducedMotion() ? 0 : 200);
  }
  function tickToast(now) {
    const paused = hovering || toast.contains(document.activeElement);
    if (!paused) remaining -= now - lastTick;
    lastTick = now;
    ring.style.strokeDashoffset = reducedMotion() ? '0' : String(1 - Math.max(0, remaining) / duration);
    if (remaining <= 0) { closeToast(); return; }
    toastFrame = requestAnimationFrame(tickToast);
  }
  function showToast(names, supplement) {
    const cfg = config();
    if (!cfg.enabled || !cfg.toastEnabled) names = [];
    if (!names.length && !supplement) return;
    cancelAnimationFrame(toastFrame); clearTimeout(hideTimer);
    const joined = names.length > 1 ? llText("ll_js_cart_1_and_2", "[1] and [2]", [names.slice(0, -1).join(', '), names.at(-1)]) : names[0];
    toastClose.setAttribute('aria-label', cfg.toastClose || llText("ll_js_cart_close", "Close"));
    toast.hidden = false;
    const products = supplement?.products || [];
    const productNames = products.map(product => product.name);
    const productName = productNames.length >= 3 ? llText("ll_js_cart_1_supplements", "[1] supplements", [productNames.length]) : productNames.join(llText("ll_js_cart_and", " and ")) || llText("ll_js_cart_supplement", "supplement");
    let title = (supplement?.title || llText("ll_js_cart_congratulations_your_name_is_free", "Congratulations! Your [name] is free")).replace('[name]', productName);
    if (productNames.length > 1) title = title.replace(/\bis free\b/, llText("ll_js_cart_are_free", "are free"));
    if (productNames.length >= 3) title = title.replace(/\bYour (?=\d)/, '');
    toast.querySelector('strong').textContent = supplement ? title : cfg.toastTitle || llText("ll_js_cart_congratulations", "Congratulations!");
    const icon = toast.querySelector('.ll-cart-toast__icon');
    icon.replaceChildren();
    const pictured = products.find(product => product.image);
    if (supplement && pictured) {
      const photo = document.createElement('img');
      photo.src = pictured.image; photo.alt = ''; photo.width = 68; photo.height = 68;
      icon.append(photo);
    } else icon.textContent = '🎉';
    const giftLine = names.length ? (cfg.unlockedLine || llText("ll_js_cart_you_unlocked_gift", "You unlocked [gift]")).replace('[gift]', joined) : '';
    toast.querySelector('p').textContent = supplement ? `${(supplement.line || llText("ll_js_cart_on_us_you_re_saving_amount", "On us. You're saving [amount]")).replace('[amount]', supplement.money)}${giftLine ? ' · ' + giftLine : ''}` : giftLine;
    duration = Math.max(3, Math.min(10, Number(cfg.toastSeconds) || 5)) * 1000;
    remaining = duration; lastTick = performance.now(); hovering = toast.matches(':hover');
    ring.style.strokeDashoffset = '0';
    positionToast();
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    toastFrame = requestAnimationFrame(tickToast);
  }
  toastClose.addEventListener('click', () => closeToast(true));
  toast.addEventListener('pointerenter', () => { hovering = true; });
  toast.addEventListener('pointerleave', () => { hovering = false; lastTick = performance.now(); });
  toast.addEventListener('focusin', event => { if (!toast.contains(event.relatedTarget)) toastReturn = event.relatedTarget; });
  toast.addEventListener('focusout', () => { lastTick = performance.now(); });
  window.addEventListener('resize', positionToast);
  window.addEventListener('scroll', positionToast, { passive: true });
  new ResizeObserver(positionToast).observe(drawer.querySelector('.sidebar__header'));
  function presentPrices() {
    document.querySelectorAll('.ll-cart [data-ll-money]').forEach(node => {
      const text = money(node.dataset.llMoney);
      if (node.textContent !== text) node.textContent = text;
    });
    const cfg = config();
    // Prices come from the same Liquid fragment as the lines, never from parsed display text.
    // Update paid/gift copies by their unique keys, including the preview-gifts flag.
    const lines = new Map((cfg.lines || []).map(line => [line.key, line]));
    document.querySelectorAll('.ll-cart [data-js-cart-item]').forEach(row => {
      const line = lines.get(row.dataset.id);
      if (!line) return;
      const price = row.querySelector('.cart-item__price');
      const value = price?.querySelector('strong');
      const freeGift = line.gift && (line.final === 0 || cfg.previewGifts === true);
      const freeSauce = !!row.dataset.llFreeSauce;
      // Marked sauces say FREE only after Shopify reports a real zero line price.
      const showFree = freeGift || (freeSauce ? line.final === 0 : (line.final === 0 && line.original > 0));
      if (value) {
        const valueText = showFree ? (cfg.free || llText("ll_js_cart_free", "FREE")) : money(line.final);
        if (value.textContent !== valueText) value.textContent = valueText;
        if (value.classList.contains('ll-cart-free') !== showFree) value.classList.toggle('ll-cart-free', showFree);
        let original = price.querySelector('del');
        if (showFree && !freeSauce && !original && line.original > 0) {
          original = document.createElement('del');
          price.append(original);
        }
        if (original) {
          const originalText = money(showFree || line.final < line.original ? line.original : line.compare);
          if (original.textContent !== originalText) original.textContent = originalText;
        }
      }
      const sauceNote = row.querySelector('[data-ll-free-sauce-note]');
      if (sauceNote) {
        const hidden = !freeSauce || line.final === 0;
        if (sauceNote.hidden !== hidden) sauceNote.hidden = hidden;
      }
      const label = row.querySelector('.ll-cart-gift-label');
      if (label && line.gift) {
        // Preview gifts share the real-zero presentation; paid basis remains untouched.
        if (label.hidden !== freeGift) label.hidden = freeGift;
        const pendingText = cfg.pending || '';
        if (label.textContent !== pendingText) label.textContent = pendingText;
        if (label.classList.contains('ll-cart-gift-pending') !== !freeGift) label.classList.toggle('ll-cart-gift-pending', !freeGift);
      }
      const unit = row.querySelector('.cart-item__unit-price');
      if (unit) {
        const text = [...unit.childNodes].find(node => node.nodeType === Node.TEXT_NODE && node.textContent.includes('/'));
        if (text) {
          const next = text.textContent.replace(/^[\s\S]*?\//, money(line.unit) + ' /');
          if (text.textContent !== next) text.textContent = next;
        }
      }
    });
    // Local renders discount captions and shipping notices outside the LL snippets.
    // Normalise rand text only in those money-bearing elements, preserving nested labels.
    drawer.querySelectorAll('.cart-item__discounts, shipping-notice, [data-cart-total]').forEach(container => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        const next = node.textContent.replace(/R\s*(\d[\d,]*(?:\.\d{2})?)/g, (_, amount) => money(Math.round(Number(amount.replaceAll(',', '')) * 100)));
        if (node.textContent !== next) node.textContent = next;
      }
    });
  }
  function rememberCartResponse(result) {
    // Cart identity comes from Ajax JSON; Liquid does not expose the cart token.
    if (result.token) cartToken = result.token;
    const section = result.sections?.[helperSectionId] ?? result.sections?.['helper-cart'];
    if (typeof section === 'string' && section) lastSectionHtml = section;
    return result;
  }
  async function request(path, body, withSections = false) {
    const result = await globalThis.LLCartSelectionCore.cartRequest({
      fetcher: window.fetch.bind(window), root, path, body,
      sectionId: withSections ? helperSectionId : undefined,
      sectionsUrl: withSections ? sectionsUrl() : undefined
    });
    return rememberCartResponse(result);
  }
  function checkoutLabel(button, text) {
    if (button.matches('input')) {
      if (!button.dataset.llDefaultLabel) button.dataset.llDefaultLabel = button.value || button.getAttribute('aria-label') || '';
      const next = text || button.dataset.llDefaultLabel;
      if (button.value !== next) button.value = next;
      return;
    }
    let label = button.querySelector('[data-ll-checkout-label]');
    if (!label) {
      label = document.createElement('span');
      label.dataset.llCheckoutLabel = '';
      const textNodes = [...button.childNodes].filter(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      label.textContent = textNodes.map(node => node.textContent).join(' ').trim();
      textNodes.forEach(node => node.remove());
      button.append(label);
    }
    if (!label.dataset.llDefaultLabel) label.dataset.llDefaultLabel = label.textContent.trim();
    const next = text || label.dataset.llDefaultLabel;
    if (label.textContent !== next) label.textContent = next;
  }
  function paint() {
    const removing = removalActive();
    const queued = mutationQueued();
    const sauceState = freeSauceState();
    const sauceWorking = sauceState.pending || sauceState.running;
    const locked = busy || queued || removing || sauceWorking || localBusy() || failed;
    if (drawer.hasAttribute('data-ll-busy') !== busy) drawer.toggleAttribute('data-ll-busy', busy);
    if (drawer.hasAttribute('data-ll-swapping') !== swapping) drawer.toggleAttribute('data-ll-swapping', swapping);
    const ariaBusy = String(busy || queued || removing || sauceWorking || localBusy());
    if (drawer.getAttribute('aria-busy') !== ariaBusy) drawer.setAttribute('aria-busy', ariaBusy);
    surfaces().forEach(surface => {
      if (surface.getAttribute('aria-busy') !== ariaBusy) surface.setAttribute('aria-busy', ariaBusy);
      if (surface.hasAttribute('data-ll-updating') !== removing) surface.toggleAttribute('data-ll-updating', removing);
      surface.querySelectorAll('[data-ll-cart-add]').forEach(button => {
        const disabled = (!swapping && locked) || button.hasAttribute('data-ll-unavailable');
        if (button.disabled !== disabled) button.disabled = disabled;
      });
    });
    // Preserve each payment brand's SVG presentation attributes over Local's blanket fill rule.
    drawer.querySelectorAll('.ll-cart-payments svg [fill]').forEach(node => {
      const fill = node.getAttribute('fill');
      if (node.style.fill !== fill) node.style.fill = fill;
    });
    surfaces().forEach(surface => {
      const hardLocked = checkoutHardLocked(surface) || (surface === drawer && drawer.classList.contains('cart-is-empty'));
      const disabled = hardLocked || checkoutTermsLocked(surface);
      surface.querySelectorAll('#CheckOut, [name="checkout"]').forEach(button => {
        if (button.hasAttribute('data-ll-checkout-locked') !== hardLocked) button.toggleAttribute('data-ll-checkout-locked', hardLocked);
        if (button.disabled !== disabled) button.disabled = disabled;
        checkoutLabel(button, sauceState.blocked ? sauceState.reason : '');
      });
      surface.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach(node => {
        if (node.hidden !== disabled) node.hidden = disabled;
        if (node.hasAttribute('aria-hidden') !== disabled) node.toggleAttribute('aria-hidden', disabled);
        if ('inert' in node && node.inert !== disabled) node.inert = disabled;
      });
      if (surface !== drawer) {
        const empty = meals(surface).items === 0;
        if (surface.classList.contains('cart-section--empty') !== empty) surface.classList.toggle('cart-section--empty', empty);
      }
    });
    surfaces().forEach(surface => {
      const status = surface.querySelector('[data-ll-cart-status]');
      const visibleMessage = sauceState.message || message;
      if (status && status.textContent !== visibleMessage) status.textContent = visibleMessage;
      const retry = surface.querySelector('[data-ll-cart-retry]');
      if (retry && retry.hidden !== !failed) retry.hidden = !failed;
    });
    paintSelection();
  }
  // Compare server-rendered gift tiers, including external Local quantity updates. Seed silently.
  let unlocked = new Set([...drawer.querySelectorAll('[data-ll-unlocked]')].map(node => node.dataset.llUnlocked));
  const supplementState = () => {
    const node = drawer.querySelector('[data-ll-b2g1-status]');
    const copy = node?.querySelector('[data-ll-b2g1-copy]');
    const units = Number(node?.dataset.units) || 0;
    const free = Math.min(Number(node?.dataset.freeUnits) || 0, Math.floor(units / 3));
    const saving = free ? Number(node?.dataset.saving) || 0 : 0;
    // Variant/plan/properties identity stays stable when Shopify changes a discounted line key.
    const products = new Map();
    if (free) node?.querySelectorAll('[data-ll-b2g1-product]').forEach(item => {
      const product = { ...item.dataset, amount:Number(item.dataset.amount) || 0 };
      product.amount += products.get(product.id)?.amount || 0;
      products.set(product.id, product);
    });
    return { units, free, saving, money:free ? node?.dataset.savingMoney || '' : '', products:[...products.values()], title:copy?.dataset.title, line:copy?.dataset.line };
  };
  let previousSupplement = supplementState(); // LL: page load is always silent.
  const celebrations = new Map();
  function celebrate() {
    const current = new Set([...drawer.querySelectorAll('[data-ll-unlocked]')].map(node => node.dataset.llUnlocked));
    const fresh = [...current].filter(tier => !unlocked.has(tier));
    unlocked = current;
    const supplement = supplementState();
    const newFree = supplement.free > previousSupplement.free && supplement.units >= previousSupplement.units && supplement.saving > 0;
    // Celebrate newly allocated amounts, so 5 → 6 names the second award.
    const earned = supplement.products.map(product => ({ ...product, amount:Math.max(0, product.amount - (previousSupplement.products.find(old => old.id === product.id)?.amount || 0)) })).filter(product => product.amount > 0);
    const award = { ...supplement, products:earned, money:money(earned.reduce((sum, product) => sum + product.amount, 0)) };
    previousSupplement = supplement;
    // One render produces one toast, even when a gift and a free supplement unlock together.
    if (fresh.length || newFree) showToast(fresh.map(tier => drawer.querySelector(`[data-ll-unlocked="${tier}"]`)?.dataset.llGiftName).filter(Boolean), newFree ? (earned.length ? award : supplement) : null);
    if (fresh.length) {
      fresh.forEach(tier => celebrations.set(tier, performance.now() + 600));
    }
    const active = [...celebrations.keys()].filter(tier => current.has(tier) && celebrations.get(tier) > performance.now());
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      surfaces().forEach(surface => active.forEach(tier => {
        surface.querySelectorAll(`[data-ll-unlocked="${tier}"], [data-ll-gift-step="${tier}"]`).forEach(node => {
          node.style.animationDuration = `${Math.max(1, celebrations.get(tier) - performance.now())}ms`;
          node.classList.add('ll-cart-celebrate');
          node.addEventListener('animationend', () => node.classList.remove('ll-cart-celebrate'), { once: true });
        });
      }));
    }
  }
  function rowError(tier, text) {
    surfaces().forEach(surface => {
      const row = surface.querySelector(`[data-ll-chosen-tier="${tier}"]`);
      if (!row) return;
      const error = document.createElement('p');
      error.className = 'll-cart-row-error';
      error.setAttribute('role', 'alert');
      error.textContent = text;
      row.append(error);
    });
  }
  async function swapGift(button) {
    if (busy || mutationQueued() || removalActive() || localBusy() || failed || uncommittedQuantity()) return;
    const cfg = config(), tier = Number(button.dataset.llGiftTier);
    const old = cfg.lines?.find(line => line.key === button.dataset.llGiftReplace && line.tier === tier);
    // Older fragments can still use the conservative path until their next refresh.
    if (!old) { run(button); return; }
    if (!cfg.enabled || !(cfg.thresholds[tier - 1] > 0) || cfg.basis < cfg.thresholds[tier - 1]) return;
    if (old.variant === Number(button.dataset.llCartAdd)) return;
    const identity = { token: cartToken };
    const wasDeclined = declined(identity, tier);
    const focusSurface = surfaces().find(surface => surface.contains(button));
    const snapshots = [];
    busy = true; swapping = true; message = '';
    surfaces().forEach(surface => {
      const row = [...surface.querySelectorAll('[data-js-cart-item]')].find(node => node.dataset.id === old.key);
      if (!row) return;
      snapshots.push([row, row.innerHTML]);
      row.setAttribute('aria-busy', 'true');
      row.classList.add('ll-cart-row-pending');
      row.querySelectorAll('[data-ll-gift-replace][aria-pressed]').forEach(pill => pill.setAttribute('aria-pressed', String(pill.dataset.llCartAdd === button.dataset.llCartAdd)));
      const img = row.querySelector('.cart-item__thumbnail img');
      if (img && button.dataset.image) { img.removeAttribute('srcset'); img.src = button.dataset.image; }
      const flavour = row.querySelector('.cart-item__variant');
      if (flavour && button.dataset.flavour) flavour.textContent = button.dataset.flavour;
    });
    paint();
    try {
      declined(identity, tier, true);
      await request('cart/change.js', { id: old.key, quantity: 0 });
      await request('cart/add.js', { items: [{ id: Number(button.dataset.llCartAdd), quantity: old.quantity || 1, properties: { _ll_gift: String(tier) } }] });
      if (!wasDeclined) restoreAutomaticGifts(identity, tier);
      message = cfg.added;
      // An existing cart discount applies to the replacement; no code visit/default pass.
      await refresh();
      // Only a changed paid basis needs reconciliation (for example a basket-wide
      // automatic discount affected by the replacement product).
      if (Number(config().basis) !== Number(cfg.basis)) {
        if (await reconcile(await request('cart.js'))) await refresh();
      }
    } catch (_) {
      failed = true; message = cfg.error;
      try {
        const truth = await request('cart.js');
        // Never leave the optimistic selection visible if the helper request also fails.
        snapshots.forEach(([row, html]) => {
          row.innerHTML = html;
          const line = truth.items.find(item => item.key === old.key || (String(item.properties?._ll_gift) === String(tier) && [old.variant, Number(button.dataset.llCartAdd)].includes(item.variant_id)));
          row.querySelectorAll('[data-ll-gift-replace][aria-pressed]').forEach(pill => pill.setAttribute('aria-pressed', String(Number(pill.dataset.llCartAdd) === line?.variant_id)));
          const selected = row.querySelector('[data-ll-gift-replace][aria-pressed="true"]');
          const image = row.querySelector('.cart-item__thumbnail img');
          if (image && selected?.dataset.image) { image.removeAttribute('srcset'); image.src = selected.dataset.image; }
          const flavour = row.querySelector('.cart-item__variant');
          if (flavour && selected?.dataset.flavour) flavour.textContent = selected.dataset.flavour;
        });
        try { await refresh(); } catch (_) { /* Keep the selection established by cart.js. */ }
      } catch (_) {
        // Truth unavailable: clear selection and block checkout until explicit retry.
        snapshots.forEach(([row, html]) => { row.innerHTML = html; row.querySelectorAll('[aria-pressed]').forEach(pill => pill.setAttribute('aria-pressed', 'false')); });
      }
      rowError(tier, message);
    } finally {
      snapshots.forEach(([row]) => { row.classList.remove('ll-cart-row-pending'); row.removeAttribute('aria-busy'); });
      busy = false; swapping = false; paint();
      if (focusSurface && !focusSurface.contains(document.activeElement)) {
        const selected = focusSurface.querySelector(`[data-ll-line-tier="${tier}"] [data-ll-cart-add="${CSS.escape(button.dataset.llCartAdd)}"][aria-pressed="true"]`);
        const row = selected?.closest('[data-js-cart-item]') || focusSurface.querySelector(`[data-ll-chosen-tier="${tier}"]`);
        (selected || row?.querySelector('button:not(:disabled), a[href]') || focusSurface.querySelector('[data-ll-cart-status]'))?.focus({ preventScroll: true });
      }
      flush();
    }
  }
  function paintSaucePicker(card) {
    const total = [...card.querySelectorAll('[data-ll-sauce-quantity]')].reduce((sum, node) => sum + Number(node.value), 0);
    const minimum = Number(card.dataset.min);
    const progress = card.querySelector('[data-ll-sauce-progress]');
    const progressText = progress.dataset.text.replace('[count]', total).replace('[min]', minimum);
    if (progress.textContent !== progressText) progress.textContent = progressText;
    const add = card.querySelector('[data-ll-sauce-add]');
    const disabled = total < minimum;
    if (add.disabled !== disabled) add.disabled = disabled;
    const addText = add.dataset.text.replace('[count]', Math.max(minimum, total));
    if (add.textContent !== addText) add.textContent = addText;
  }
  async function changeOffer(button) {
    if (busy || mutationQueued() || removalActive() || localBusy() || failed || uncommittedQuantity()) return;
    const focusSurface = surfaces().find(surface => surface.contains(button));
    busy = true; message = ''; paint();
    try {
      let cart;
      if (button.matches('[data-ll-sauce-add]')) {
        const card = button.closest('[data-ll-sauce-picker]');
        const items = [...card.querySelectorAll('[data-ll-sauce-choice]')].map(row => ({ id: Number(row.dataset.llSauceChoice), quantity: Number(row.querySelector('output').value) })).filter(item => item.quantity > 0);
        if (items.reduce((sum, item) => sum + item.quantity, 0) < Number(card.dataset.min)) return;
        await request('cart/add.js', { items });
        cart = await request('cart.js');
      } else {
        // Shopify requires a line index for selling plans. Resolve it from fresh keys,
        // never from the visually regrouped DOM order (or an old server index).
        const before = await request('cart.js');
        const index = before.items.findIndex(item => item.key === button.dataset.llSubscribe);
        if (index < 0) throw new Error(config().error);
        cart = await request('cart/change.js', { line: index + 1, quantity: before.items[index].quantity, selling_plan: button.getAttribute('aria-pressed') === 'true' ? null : Number(button.dataset.plan) });
      }
      // Paid basis can change: retain the existing gift eligibility/money guard.
      await reconcile(cart);
      await refresh();
    } catch (_) {
      failed = true; message = config().error;
      try { await refresh(); } catch (_) { /* Explicit retry is required. */ }
    } finally {
      busy = false; paint();
      if (focusSurface && !focusSurface.contains(document.activeElement)) focusSurface.querySelector('[data-ll-cart-status]')?.focus({ preventScroll: true });
      flush();
    }
  }
  function renderCartHtml(html) {
    ownRender = true;
    try { window.renderHelperCart(html); } finally { ownRender = false; }
    drawer.querySelector('#AjaxCartForm').dispatchEvent(new Event('cart-updated'));
  }
  async function renderLatestSection() {
    const html = lastSectionHtml;
    lastSectionHtml = '';
    if (!html) { await refresh(); return; }
    renderCartHtml(html);
  }
  async function refresh() {
    // LL: page responses carry grouped page/drawer templates; other routes return the drawer.
    const response = await fetch(`${root}${document.querySelector('[data-ll-cart-page]') ? 'cart' : ''}?section_id=helper-cart`, { cache: 'no-store' });
    if (!response.ok) throw new Error(config().error);
    const html = await response.text();
    renderCartHtml(html);
  }
  function enqueueCartMutation(task, context = {}) {
    if (typeof task !== 'function') return Promise.reject(new TypeError('Cart mutation must be a function'));
    return new Promise((resolve, reject) => {
      queuedMutations.push({ task, context, resolve, reject });
      pending = true;
      paint();
      queueMicrotask(flush);
    });
  }
  window.LLCartMutations = Object.freeze({
    enqueue: enqueueCartMutation,
    active: () => busy || mutationQueued() || removalActive() || localBusy()
  });
  document.dispatchEvent(new CustomEvent('ll:cart:mutation-queue-ready'));
  async function applyGiftDiscount(cfg) {
    if (!cfg.discount) return;
    const response = await fetch(`${root}discount/${encodeURIComponent(cfg.discount)}`);
    if (!response.ok) throw new Error(cfg.error);
    // The discount redirect changes totals after any bundled add response.
    lastSectionHtml = '';
  }
  async function addDefaults(cart, cfg, withSections = false) {
    if (!cfg.autoAdd || !cfg.giftsLive) return false;
    const items = [];
    for (const tier of [1, 2, 3]) {
      if (!(cfg.thresholds[tier - 1] > 0) || basis(cart) < cfg.thresholds[tier - 1] || declined(cart, tier)) continue;
      let room = (tier === 3 ? 2 : 1) - tierCount(cart, tier);
      const existing = cart.items.filter(item => String(item.properties?._ll_gift) === String(tier));
      // Match occupied defaults first; a customer's alternative occupies a remaining slot.
      const defaults = [...(cfg.defaults?.[tier - 1] || [])];
      let alternatives = 0;
      for (const item of existing) for (let n = 0; n < item.quantity; n++) {
        const index = defaults.findIndex(id => id && Number(id) === Number(item.variant_id || item.id));
        if (index >= 0) defaults.splice(index, 1); else alternatives++;
      }
      defaults.splice(0, alternatives);
      for (const id of defaults) {
        if (room <= 0) break;
        if (!id) continue;
        items.push({ id: Number(id), quantity: 1, properties: { _ll_gift: String(tier) } });
        room--;
      }
    }
    if (!items.length) return false;
    await request('cart/add.js', { items }, withSections);
    await applyGiftDiscount(cfg);
    message = cfg.added;
    return true;
  }
  async function reconcile(cart, withSections = false) {
    const cfg = config();
    if (!cfg.enabled) return false;
    let removed = false;
    let defaultsAttempted = false;
    // Re-read Shopify's response after each change: line keys can change with discounts.
    for (let guard = 0; guard < 100; guard++) {
      const counts = [0, 0, 0];
      let change;
      for (const item of cart.items) {
        const tier = Number(item.properties?._ll_gift);
        if (![1, 2, 3].includes(tier)) continue;
        const limit = tier === 3 ? 2 : 1;
        const threshold = cfg.thresholds[tier - 1];
        const eligible = threshold > 0 && basis(cart) >= threshold;
        const quantity = eligible ? Math.min(item.quantity, Math.max(0, limit - counts[tier - 1])) : 0;
        counts[tier - 1] += quantity;
        if (quantity !== item.quantity) { change = { id: item.key, quantity }; break; }
      }
      if (!change) {
        if (!defaultsAttempted) {
          defaultsAttempted = true;
          if (await addDefaults(cart, cfg, withSections)) {
            removed = true;
            cart = await request('cart.js');
            continue; // Discounts may have changed the eligibility basis.
          }
        }
        return removed;
      }
      cart = await request('cart/change.js', change, withSections);
      removed = true;
      message = cfg.removed;
    }
    throw new Error(cfg.error);
  }
  const removeErrorText = "Couldn't remove that, please try again.";
  function removalEntries(keys, allowProtected = false) {
    const wanted = new Set(keys || []);
    const allowed = allowProtected ? null : new Set(globalThis.LLCartSelectionCore.selectableKeys(cartModel()));
    return (config().lines || []).filter(line => wanted.has(line.key) && (!allowed || allowed.has(line.key))).map(line => {
      const identity = globalThis.LLCartSelectionCore.lineIdentity(line);
      return {
        key: line.key,
        originalKey: line.key,
        previousKeys: [],
        identity,
        identityKey: globalThis.LLCartSelectionCore.identityKey({ identity, key: line.key }),
        quantity: Number(line.quantity) || 1,
        tier: Number(line.tier) || 0,
        protected: !!line.protected
      };
    });
  }
  function entryKeys(entry) {
    return [...new Set([entry.originalKey, ...(entry.previousKeys || []), entry.key].filter(Boolean))];
  }
  function resolveRemovalBatch(entries, cart) {
    const resolved = globalThis.LLCartSelectionCore.resolveRemovalEntries(entries, cart);
    resolved.forEach((line, index) => {
      const entry = entries[index];
      if (!Array.isArray(entry.previousKeys)) entry.previousKeys = [];
      if (line.key && line.key !== entry.key) {
        if (!entry.previousKeys.includes(entry.key)) entry.previousKeys.push(entry.key);
        entry.key = line.key;
      }
      entry.quantity = line.quantity;
      entry.missing = line.missing;
    });
    return entries.filter(entry => !entry.missing);
  }
  function headerCounts() {
    return [...document.querySelectorAll('[data-header-cart-count]')];
  }
  function setHeaderCount(node, value) {
    const text = String(Math.max(0, value));
    if (node.textContent !== text) node.textContent = text;
  }
  function captureHeaderBase() {
    optimisticHeaderBase = headerCounts().map(node => ({ node, value: Number(node.textContent) || 0 }));
  }
  function markRemoval(entry) {
    const nodes = [];
    surfaces().forEach(surface => {
      const row = [...surface.querySelectorAll('[data-js-cart-item]')].find(node => node.dataset.id === entry.key);
      const prompt = [...surface.querySelectorAll('[data-ll-sauce-flavours-for]')].find(node => node.dataset.llSauceFlavoursFor === entry.key);
      [row, prompt].filter(Boolean).forEach(node => {
        node.style.maxHeight = `${Math.ceil(node.getBoundingClientRect().height)}px`;
        void node.offsetHeight;
        node.classList.add('ll-cart-removing');
        if (node.getAttribute('aria-busy') !== 'true') node.setAttribute('aria-busy', 'true');
        nodes.push(node);
      });
    });
    return nodes;
  }
  function unmarkRemoval(state) {
    (state?.nodes || []).forEach(node => {
      if (!node.isConnected) return;
      node.classList.remove('ll-cart-removing');
      node.removeAttribute('aria-busy');
      setTimeout(() => { if (node.isConnected && !node.classList.contains('ll-cart-removing')) node.style.removeProperty('max-height'); }, 170);
    });
  }
  function updateOptimisticHeader() {
    if (!optimisticHeaderBase) return;
    const quantity = [...optimisticRemovals.values()].reduce((sum, state) => sum + (state.entry.missing ? 0 : state.entry.quantity), 0);
    optimisticHeaderBase.forEach(({ node, value }) => { if (node.isConnected) setHeaderCount(node, value - quantity); });
  }
  function rebaseOptimisticRows() {
    captureHeaderBase();
    const states = [...optimisticRemovals.values()];
    resolveRemovalBatch(states.map(state => state.entry), { items: config().lines || [] });
    optimisticRemovals.forEach(state => { state.nodes = markRemoval(state.entry); });
    updateOptimisticHeader();
  }
  function showRemovalError(entry) {
    surfaces().forEach(surface => {
      const row = [...surface.querySelectorAll('[data-js-cart-item]')].find(node => node.dataset.id === entry.key);
      if (!row || row.nextElementSibling?.matches(`[data-ll-remove-error-for="${CSS.escape(entry.key)}"]`)) return;
      const error = document.createElement('p');
      error.className = 'll-cart-remove-error';
      error.dataset.llRemoveErrorFor = entry.key;
      error.setAttribute('role', 'alert');
      error.textContent = removeErrorText;
      row.insertAdjacentElement('afterend', error);
    });
  }
  function queueRemovals(keys, context = {}) {
    const entries = removalEntries(keys, context.mode === 'all' || context.allowProtected === true);
    if (!entries.length) return;
    if (context.direct && context.mode !== 'all') entries.forEach(entry => {
      if (!entry.tier) return;
      const identity = { token: cartToken };
      entry.wasDeclined = declined(identity, entry.tier);
      declined(identity, entry.tier, true);
    });
    removalQueue.enqueue(entries, context);
  }
  function clearOptimisticEntries(entries, contexts, truth) {
    const resolved = truth ? globalThis.LLCartSelectionCore.resolveRemovalEntries(entries, truth) : [];
    entries.forEach((entry, index) => {
      const state = optimisticRemovals.get(entry.identityKey);
      unmarkRemoval(state);
      optimisticRemovals.delete(entry.identityKey);
      entryKeys(entry).forEach(key => selectedLineKeys.delete(key));
      if (truth && contexts.some(context => context.selection) && !resolved[index]?.missing) selectedLineKeys.add(resolved[index].key);
    });
    if (optimisticRemovals.size) rebaseOptimisticRows();
    else optimisticHeaderBase = undefined;
  }
  function displayRemovalError(entries, contexts) {
    const selectionContext = contexts.find(context => context.selection);
    if (selectionContext) {
      document.querySelectorAll('[data-ll-cart-selection-error]').forEach(error => {
        if (error.textContent !== removeErrorText) error.textContent = removeErrorText;
        if (error.hidden) error.hidden = false;
      });
    } else entries.forEach(showRemovalError);
    message = removeErrorText;
  }
  function rollbackRemoval(entries, contexts) {
    entries.forEach(entry => {
      const state = optimisticRemovals.get(entry.identityKey);
      unmarkRemoval(state);
      optimisticRemovals.delete(entry.identityKey);
      if (entry.tier && entry.wasDeclined === false) restoreAutomaticGifts({ token: cartToken }, entry.tier);
    });
    displayRemovalError(entries, contexts);
    if (optimisticRemovals.size) updateOptimisticHeader();
    else {
      optimisticHeaderBase?.forEach(({ node, value }) => { if (node.isConnected) setHeaderCount(node, value); });
      optimisticHeaderBase = undefined;
    }
    setTimeout(() => { paintSelection(); paint(); }, 0);
  }
  async function recoverRemovalTruth(entries, contexts) {
    // The delete response or a follow-up step was ambiguous. Keep checkout locked
    // until an explicit retry, even after server truth has been rendered.
    failed = true;
    displayRemovalError(entries, contexts);
    paint();
    try {
      const truth = await request('cart.js');
      await refresh();
      clearOptimisticEntries(entries, contexts, truth);
      paintSelection();
      paint();
      return truth;
    } catch (_) {
      // Leave the optimistic state in place; failed keeps every checkout path locked.
      paint();
      return null;
    }
  }
  async function refreshAfterRemovalQueue() {
    // Clear external-render work already seen: this request starts later and its
    // section is authoritative. A render that arrives during it sets pending again.
    pending = false;
    pendingRefresh = false;
    busy = true;
    paint();
    try {
      await refresh();
    } catch (_) {
      await recoverRemovalTruth([], []);
    } finally {
      busy = false;
      paint();
      if (pending && !failed) queueMicrotask(flush);
    }
  }
  removalQueue = globalThis.LLCartSelectionCore.createRemovalQueue({
    wait: 70,
    onOptimistic(entries) {
      if (!optimisticRemovals.size) captureHeaderBase();
      entries.forEach(entry => {
        entryKeys(entry).forEach(key => document.querySelectorAll(`[data-ll-remove-error-for="${CSS.escape(key)}"]`).forEach(node => node.remove()));
        optimisticRemovals.set(entry.identityKey, { entry, nodes: markRemoval(entry) });
      });
      updateOptimisticHeader();
      paint();
    },
    async commit(entries, contexts) {
      lastSectionHtml = '';
      const clear = contexts.some(context => context.mode === 'all');
      let writeAttempted = false;
      let writeAccepted = false;
      try {
        // Discount allocation can change every line key. Resolve the stable
        // variant/plan/properties identity immediately before each queued write.
        const before = await request('cart.js');
        const currentEntries = resolveRemovalBatch(entries, before);
        if (!clear && !currentEntries.length) return { cart: before, writeAccepted: true };
        writeAttempted = true;
        const cart = clear
          ? await request('cart/clear.js', {}, true)
          : await request('cart/update.js', { updates: Object.fromEntries(currentEntries.map(entry => [entry.key, 0])) }, true);
        writeAccepted = true;
        if (!clear) await reconcile(cart, true);
        return { cart, writeAccepted };
      } catch (error) {
        error.llRemovalWriteAccepted = writeAccepted;
        error.llRemovalKnownNotApplied = !writeAccepted && (!writeAttempted || error.cartKnownNotApplied === true);
        throw error;
      }
    },
    async onSuccess(result, entries, contexts) {
      const cart = result.cart;
      message = '';
      try {
        await renderLatestSection();
        clearOptimisticEntries(entries, contexts);
      } catch (_) {
        await recoverRemovalTruth(entries, contexts);
      }
      const selectionContext = contexts.find(context => context.selection);
      if (selectionContext) {
        const focusTarget = selectionContext.onPage
          ? document.querySelector('[data-ll-cart-page] [data-ll-delete-selected]:not(:disabled), [data-ll-cart-page] [data-ll-delete-all]:not(:disabled), .ll-cart-page-continue')
          : drawer.querySelector('[data-ll-delete-selected]:not(:disabled), [data-ll-delete-all]:not(:disabled), [data-js-close]');
        focusTarget?.focus({ preventScroll: true });
      }
      window.KROWN?.functions?.dispatchStandardEvent?.('CartLinesUpdateEvent', drawer, {
        action: 'remove', context: 'cart', lines: entries.map(entry => ({ id: entry.key, quantity: 0 })), promise: Promise.resolve({ cart })
      });
      setTimeout(() => { paintSelection(); paint(); }, 0);
    },
    async onError(error, entries, contexts) {
      if (error.llRemovalKnownNotApplied) rollbackRemoval(entries, contexts);
      else await recoverRemovalTruth(entries, contexts);
    },
    async onIdle({ outcome }) {
      // The bundled response is fast, but a product-card add can finish while a
      // delete is active. One post-drain truth pass makes the final section win.
      if (outcome === 'success' && !failed) await refreshAfterRemovalQueue();
      else if (pendingRefresh && !failed) queueMicrotask(flush);
    }
  });
  async function addSauceFlavour(button) {
    if (busy || mutationQueued() || removalActive() || localBusy() || failed || uncommittedQuantity() || button.disabled) return;
    const focusSurface = surfaces().find(surface => surface.contains(button));
    busy = true; message = ''; lastSectionHtml = ''; paint();
    try {
      let cart = await globalThis.LLCartSelectionCore.addFlavour({
        fetcher: window.fetch.bind(window), root, variantId: button.dataset.llSauceFlavourAdd,
        available: !button.hasAttribute('data-ll-unavailable'), sectionId: helperSectionId, sectionsUrl: sectionsUrl()
      });
      cart = rememberCartResponse(cart);
      await reconcile(cart, true);
      await renderLatestSection();
    } catch (_) {
      failed = true; message = config().error;
      try { await refresh(); } catch (_) { /* Explicit retry is required. */ }
    } finally {
      busy = false; paint();
      if (focusSurface && !focusSurface.contains(document.activeElement)) focusSurface.querySelector('[data-ll-cart-status]')?.focus({ preventScroll: true });
      flush();
    }
  }
  async function run(button, forceRefresh = false) {
    const retrying = failed;
    if (busy || localBusy() || (!retrying && removalActive()) || (button && mutationQueued())) {
      pending = true;
      pendingRefresh = pendingRefresh || forceRefresh || !button;
      return;
    }
    const queuedMutation = button ? null : queuedMutations.shift();
    if (mutationQueued()) pending = true;
    let queuedMutationSettled = false;
    busy = true;
    failed = false;
    if (retrying) { message = ''; }
    paint();
    const focusSurface = surfaces().find(surface => surface.contains(document.activeElement));
    try {
      let cart = await request('cart.js');
      if (button) {
        message = '';
        const tier = Number(button.dataset.llGiftTier);
        const cfg = config();
        const old = cart.items.find(item => item.key === button.dataset.llGiftReplace && String(item.properties?._ll_gift) === String(tier));
        const removing = button.dataset.llGiftRemove;
        const eligible = [1, 2, 3].includes(tier) && cfg.enabled && cfg.thresholds[tier - 1] > 0 && basis(cart) >= cfg.thresholds[tier - 1];
        const validGift = eligible && (old || tierCount(cart, tier) < (tier === 3 ? 2 : 1));
        if (removing) {
          const line = cart.items.find(item => item.key === removing && String(item.properties?._ll_gift) === String(tier));
          if (line) {
            declined(cart, tier, true);
            cart = await request('cart/change.js', { id: line.key, quantity: 0 });
          }
        } else if ((!tier || validGift) && (!button.dataset.llGiftReplace || old)) {
          const item = { id: Number(button.dataset.llCartAdd), quantity: 1 };
          if (tier) item.properties = { _ll_gift: String(tier) };
          const wasDeclined = tier ? declined(cart, tier) : false;
          if (old) {
            // Retain the customer's choice if a swap add fails after removal.
            declined(cart, tier, true);
            cart = await request('cart/change.js', { id: old.key, quantity: 0 });
          }
          await request('cart/add.js', { items: [item] });
          if (old && !wasDeclined) restoreAutomaticGifts(cart, tier);
          message = cfg.added;
          if (tier) await applyGiftDiscount(cfg);
          cart = await request('cart.js');
        }
      }
      if (queuedMutation) {
        const result = await queuedMutation.task({
          request,
          refresh,
          cart,
          context: queuedMutation.context
        });
        queuedMutation.resolve(result);
        queuedMutationSettled = true;
        cart = result?.cart || await request('cart.js');
      }
      const removed = await reconcile(cart);
      if (button || removed || retrying || forceRefresh) await refresh();
      if (retrying && optimisticRemovals.size) clearOptimisticEntries([...optimisticRemovals.values()].map(state => state.entry), [], cart);
    } catch (error) {
      if (queuedMutation && !queuedMutationSettled) {
        queuedMutation.reject(error);
        queuedMutationSettled = true;
      }
      failed = true;
      message = config().error;
      // A write may have succeeded even when its response failed. Refresh the truth;
      // never retry an add automatically, which could duplicate a paid line.
      try { await refresh(); } catch (_) { /* Keep checkout blocked until explicit retry. */ }
    } finally {
      busy = false;
      paint();
      if (focusSurface && !focusSurface.contains(document.activeElement)) focusSurface.querySelector('[data-ll-cart-status]')?.focus({ preventScroll: true });
      if (pending && !failed) queueMicrotask(flush);
    }
  }
  document.addEventListener('ll:cart:rendered', () => {
    decorateShell();
    presentPrices();
    celebrate();
    updateGiftHeaders();
    initAddrows();
    paint();
    if (removalActive()) rebaseOptimisticRows();
    const anchor = actionAnchor; actionAnchor = null;
    restoreAnchor(anchor);
    requestAnimationFrame(() => restoreAnchor(anchor));
    positionToast();
    if (!ownRender) { pending = true; pendingRefresh = true; queueMicrotask(flush); }
  });
  document.addEventListener('ll:free-sauce:state', paint);
  function flush() {
    if (pending && !busy && !removalActive() && !localBusy() && !failed) {
      const forceRefresh = pendingRefresh;
      pending = false;
      pendingRefresh = false;
      run(undefined, forceRefresh);
    }
  }
  document.addEventListener('change', event => {
    const lineSelect = event.target.closest('[data-ll-line-select]');
    if (lineSelect) {
      if (lineSelect.checked) selectedLineKeys.add(lineSelect.dataset.llLineSelect);
      else selectedLineKeys.delete(lineSelect.dataset.llLineSelect);
      const bar = lineSelect.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart')?.querySelector('[data-ll-cart-selection]');
      closeDeleteConfirm(bar);
      paintSelection();
      return;
    }
    const selectAll = event.target.closest('[data-ll-select-all]');
    if (selectAll) {
      const surface = selectAll.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart');
      selectableRows(surface).forEach(row => {
        if (selectAll.checked) selectedLineKeys.add(row.dataset.id);
        else selectedLineKeys.delete(row.dataset.id);
      });
      closeDeleteConfirm(selectAll.closest('[data-ll-cart-selection]'));
      paintSelection();
      return;
    }
    const select = event.target.closest('[data-ll-addrow-variant]');
    if (!select || !select.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart')) return;
    const card = select.closest('[data-ll-product]');
    const option = select.selectedOptions[0];
    const add = card?.querySelector('[data-ll-cart-add]');
    if (add) add.dataset.llCartAdd = option.value;
    const image = card?.querySelector('.ll-cart-addrow__media img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
      image.alt = option.dataset.alt || '';
    }
    const price = card?.querySelector('[data-ll-money]');
    if (price) {
      price.dataset.llMoney = option.dataset.price;
      price.textContent = money(option.dataset.price);
    }
  });
  document.addEventListener('keydown', event => {
    const track = event.target.closest?.('[data-ll-addrow-track]');
    if (!track || event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    scrollAddrow(track, event.key === 'ArrowLeft' ? -1 : 1);
  });
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart')) return;
    const target = event.target.closest('button, a, input');
    if (!target) return;
    if (target.matches('[data-js-close], [data-ll-cart-continue]')) {
      if (target.matches('[data-ll-cart-continue]')) { event.preventDefault(); drawer.hide(); }
      return;
    }
    if (target.matches('.remove') && !busy && !mutationQueued() && !localBusy() && !failed) {
      const row = target.closest('[data-js-cart-item]');
      if (row?.dataset.id) {
        event.preventDefault(); event.stopImmediatePropagation();
        actionAnchor = captureAnchor(target);
        const freeSauceOrdinal = Number(target.dataset.llFreeSauceRemove || row.dataset.llFreeSauce);
        if (freeSauceOrdinal) {
          document.dispatchEvent(new CustomEvent('ll:free-sauce:remove-request', { detail: { ordinal: freeSauceOrdinal } }));
          return;
        }
        queueRemovals([row.dataset.id], { direct: true, allowProtected: true });
        return;
      }
    }
    if (target.matches('[data-ll-sauce-flavour-add]') && !target.disabled) {
      event.preventDefault(); event.stopImmediatePropagation();
      addSauceFlavour(target);
      return;
    }
    if (target.matches('[data-ll-addrow-arrow]') && !target.disabled) {
      event.preventDefault();
      scrollAddrow(target, Number(target.dataset.llAddrowArrow));
      return;
    }
    if ((busy || mutationQueued() || removalActive() || localBusy() || failed) && !target.matches('[data-ll-cart-retry]')) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    if (target.matches('[data-ll-delete-selected], [data-ll-delete-all]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      openDeleteConfirm(target, target.matches('[data-ll-delete-all]') ? 'all' : 'selected');
      return;
    }
    if (target.matches('[data-ll-delete-confirm-cancel]')) {
      event.preventDefault();
      closeDeleteConfirm(target.closest('[data-ll-cart-selection]'), true);
      return;
    }
    if (target.matches('[data-ll-delete-confirm-yes]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      confirmSelection(target.closest('[data-ll-cart-selection]'));
      return;
    }
    if (target.matches('[data-ll-cart-add], [data-ll-sauce-add], [data-ll-subscribe], .qty-minus, .qty-plus')) actionAnchor = captureAnchor(target);
    if (target.matches('[data-ll-variant]') && !target.disabled) {
      const card = target.closest('.ll-cart-offer');
      card.querySelectorAll('[data-ll-variant]').forEach(pill => pill.setAttribute('aria-pressed', String(pill === target)));
      card.querySelector('[data-ll-cart-add]').dataset.llCartAdd = target.dataset.llVariant;
      const image = card.querySelector('img');
      if (image && target.dataset.image) { image.removeAttribute('srcset'); image.src = target.dataset.image; }
      const price = card.querySelector('[data-ll-money]');
      if (price) { price.dataset.llMoney = target.dataset.price; price.textContent = money(target.dataset.price); }
    }
    if (target.matches('[data-ll-cart-add]') && !target.disabled) {
      event.preventDefault();
      if (target.matches('[data-ll-addrow-add]')) {
        addFromAddrow(target);
      } else if (target.dataset.llGiftReplace) {
        if (target.getAttribute('aria-pressed') !== 'true') swapGift(target);
      } else run(target);
    }
    if (target.matches('[data-ll-sauce-step]') && !target.disabled) {
      event.preventDefault();
      const row = target.closest('[data-ll-sauce-choice]');
      const count = row.querySelector('output');
      const value = Math.max(0, Number(count.value) + Number(target.dataset.llSauceStep));
      if (Number(count.value) !== value) count.value = value;
      const minus = row.querySelector('[data-ll-sauce-step="-1"]');
      const disabled = value === 0;
      if (minus.disabled !== disabled) minus.disabled = disabled;
      paintSaucePicker(row.closest('[data-ll-sauce-picker]'));
    }
    if (target.matches('[data-ll-sauce-add], [data-ll-subscribe]') && !target.disabled) { event.preventDefault(); changeOffer(target); }
    if (target.matches('[data-ll-cart-retry]')) { event.preventDefault(); run(); }
  }, true);
  // Capture protects against Local terms code or another script re-enabling checkout.
  const guardCheckout = event => {
    const origin = event.type === 'submit' && event.submitter ? event.submitter : event.target;
    const surface = origin.closest('[data-ll-cart-page], #site-cart-sidebar.ll-cart');
    if (!surface) return;
    const checkout = event.type === 'submit' ? event.target.matches('form.cart__form') || event.submitter?.matches('[name="checkout"]') : event.target.closest("[name=\"checkout\"], #CheckOut");
    if (!checkout || b2g1ReminderBypass) return;
    if (checkoutLocked(surface)) { event.preventDefault(); event.stopImmediatePropagation(); paint(); return; }
    if (shouldShowB2g1Reminder(surface)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openB2g1Reminder(surface, event.submitter || (checkout instanceof Element ? checkout : origin));
    }
  };
  document.addEventListener('submit', guardCheckout, true);
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('change', () => queueMicrotask(paint));
  document.addEventListener('input', event => { if (event.target.matches('.cart__form .qty')) paint(); });
  // Local terms toggles every #CheckOut; restore each surface's combined gate immediately.
  const gateObserver = new MutationObserver(paint);
  surfaces().forEach(surface => gateObserver.observe(surface, {subtree: true, attributes: true, attributeFilter: ['disabled']}));
  const pageSurface = document.querySelector('[data-ll-cart-page]');
  if (pageSurface) new MutationObserver(() => { flush(); paint(); }).observe(pageSurface, {subtree: true, attributes: true, attributeFilter: ['class']});
  // Local owns Escape/show/hide. Keep the focus loop current after fragment replacement.
  document.addEventListener('keydown', event => {
    if (!drawer.classList.contains('sidebar--opened') || (!drawer.contains(event.target) && !toast.contains(event.target))) return;
    if (event.key !== 'Tab') return;
    const controls = [...drawer.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), textarea, summary, [tabindex="0"]')].filter(node => node.getClientRects().length);
    if (!toast.hidden) controls.push(toastClose);
    const first = controls[0], last = controls.at(-1);
    if (!first) { event.preventDefault(); drawer.focus({ preventScroll: true }); return; }
    if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) { event.preventDefault(); last.focus({ preventScroll: true }); }
    else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement))) { event.preventDefault(); first.focus({ preventScroll: true }); }
  });
  new MutationObserver(() => {
    const isOpen = drawer.classList.contains('sidebar--opened');
    if (isOpen !== opened) {
      opened = isOpen;
      document.querySelectorAll('[aria-controls="site-cart-sidebar"]').forEach(node => {
        const expanded = String(opened);
        if (node.getAttribute('aria-expanded') !== expanded) node.setAttribute('aria-expanded', expanded);
      });
      if (opened) { previousFocus = document.activeElement; drawer.querySelector('[data-js-close]')?.focus({ preventScroll: true }); }
      else { if (drawer.getAttribute('aria-hidden') !== 'true') drawer.setAttribute('aria-hidden', 'true'); if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true }); }
    }
    positionToast();
    syncDockHeight();
    flush();
    // Do not observe attributes changed by paint(), avoiding an observer feedback loop.
    paint();
  }).observe(drawer, { subtree: true, attributes: true, attributeFilter: ['class'] });
  window.addEventListener('resize', () => {
    syncDockHeight();
    document.querySelectorAll('[data-ll-addrow-track]').forEach(scheduleAddrow);
  });
  decorateShell();
  presentPrices();
  updateGiftHeaders();
  initAddrows();
  initB2g1Reminder();
  paint();
  run();
})();
